"use client";

import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import InfiniteScroll from "@/components/InfiniteScroll";
import apiFetch from "@/lib/fetch";
import { useCallback, useEffect, useRef, useState } from "react";
import { VenueLoading } from "../list/venue-list/Venue";
import ListCard from "@/components/views/list/card/VenueCard";
import { useCompanyContext } from "@/context/CompanyContext";
import { useHeader } from "@/hooks/headerContext";
import { useUserLocation } from "@/context/UserLocationContext";

export default function CompanyVenues({ company, initialData = [] }: { company: string, initialData: any[] }) {
  const initialList = Array.isArray(initialData) ? initialData : [];
  const [index, setIndex] = useState(0);
  const [data, setData] = useState<any[]>(initialList);
  const [hasMore, setHasMore] = useState(initialList.length >= DEFAULT_DATA_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const { companyFilters, setCompanyFilters } = useCompanyContext();
  const { service, district } = useHeader();

  const serviceId = companyFilters?.service?.id || null;
  const districtId = companyFilters?.district?.id || null;
  const filterKey = `${serviceId ?? ""}|${districtId ?? ""}`;

  // initialData is rendered on the server for the initial filters, so the first
  // client fetch only happens once the filters actually change.
  const loadedFilterKey = useRef(filterKey);
  // Only the latest request may write to state, so a slow page response can't
  // overwrite the list after the filters changed.
  const requestId = useRef(0);

  const {
    locationPermission,
    userLocation,
    venueDistances,
    calculateDistances,
  } = useUserLocation();

  const calculatedVenueIds = useRef<Set<number>>(new Set());

  // Calculate distances when user location is available
  useEffect(() => {
    if (locationPermission === "granted" && userLocation && data.length > 0) {
      const venuesToCalculate = data
        .filter((venue: any) => {
          const hasCoords = venue.map_latitude && venue.map_longitude;
          const notCalculated = !calculatedVenueIds.current.has(venue.id);
          return hasCoords && notCalculated;
        })
        .map((venue: any) => ({
          id: venue.id,
          lat: parseFloat(venue.map_latitude),
          lng: parseFloat(venue.map_longitude),
        }));

      if (venuesToCalculate.length > 0) {
        venuesToCalculate.forEach((v: { id: number }) => calculatedVenueIds.current.add(v.id));
        calculateDistances(venuesToCalculate);
      }
    }
  }, [locationPermission, userLocation, data, calculateDistances]);

  const fetchPage = useCallback(async (page: number, append: boolean) => {
    const currentRequest = ++requestId.current;
    setIsLoading(true);

    let url = `company/venues?index=${page}&company=${company}`;
    if (serviceId) url += `&service=${serviceId}`;
    if (districtId) url += `&district=${districtId}`;

    try {
      const response = await apiFetch({ route: url });
      if (currentRequest !== requestId.current) return;

      // An error payload (rate limit, 5xx) is an object, not a list: keep the
      // cards already on screen and stop paging instead of blanking the page.
      if (!Array.isArray(response)) {
        setHasMore(false);
        return;
      }

      setData(prev => {
        if (!append) return response;
        // Pages can overlap on the API side; duplicate keys break React's list rendering.
        const seen = new Set(prev.map((venue: any) => venue.id));
        return prev.concat(response.filter((venue: any) => !seen.has(venue.id)));
      });
      setIndex(page);
      setHasMore(response.length >= DEFAULT_DATA_LIMIT);
    } catch {
      // Network failure or a stale server action after a deploy.
      if (currentRequest === requestId.current) setHasMore(false);
    } finally {
      if (currentRequest === requestId.current) setIsLoading(false);
    }
  }, [company, serviceId, districtId]);

  // Follow the header's service/district selection.
  useEffect(() => {
    setCompanyFilters(prev => ({
      ...prev,
      service: {
        title: service?.title || null,
        id: service?.id || null
      },
      district: {
        title: district?.title || null,
        id: district?.id || null
      }
    }));
  }, [district, service, setCompanyFilters]);

  // A filter change starts the list over from the first page.
  useEffect(() => {
    if (filterKey === loadedFilterKey.current) return;
    loadedFilterKey.current = filterKey;

    setHasMore(true);
    fetchPage(0, false);
  }, [filterKey, fetchPage]);

  return (
    <InfiniteScroll
      hasMore={hasMore}
      next={() => fetchPage(index + 1, true)}
      loading={
        <div className="container">
          <VenueLoading count={24} />
        </div>
      }
      isLoading={isLoading}
      skeletonElementId="venue-card-skeleton"
      dataLength={data.length}
    >
      <div className="container">
        <div className="xl:grid justify-items-center flex flex-wrap justify-center xl:grid-cols-3 gap-5 py-4">
          {data.map((item: any) => {
            const distance = venueDistances.get(item.id);
            return (
              <ListCard
                titleHeadingLevel="h2"
                key={item.id}
                {...item}
                distance={distance}
                type="company"
                companyDatas={item.company}
              />
            );
          })}
        </div>
      </div>
    </InfiniteScroll>
  );
}
